const ModalAddExercise = () => import(/* webpackPrefetch: true, webpackChunkName: "modal-add-exercise" */ '@/app/views/main-view/_shared/_components/pet-exercise-component/modals/modal-add-exercise/modal-add-exercise.vue');
const ModalEditDeleteExercise = () => import(/* webpackPrefetch: true, webpackChunkName: "modal-edit-delete-exercise" */ '@/app/views/main-view/_shared/_components/pet-exercise-component/modals/modal-edit-delete-exercise/modal-edit-delete-exercise.vue');

// Services
import exerciseService from '@/app/services/main/exercise/exercise-service.js';

// Mixins
import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { userMixins } from '@/app/mixins/modules/user-mixins.js';
import { tableMixins } from '@/app/mixins/utils/table-mixins.js';
import { switchPetMixins } from '@/app/mixins/modules/switch-pet-mixins';
import { toastMessageMixins } from '@/app/mixins/utils/toast-message-mixins';
import { dateMixins } from '@/app/mixins/utils/date-mixins';

// Constant
import { STATUS_CODES } from '@/app/constants/status-constants';

// Third party libraries
import DatePicker from 'vue2-datepicker';
import { Bar } from 'vue-chartjs/legacy';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale
} from 'chart.js'
  
ChartJS.register(Title, Tooltip, Legend, CategoryScale, BarElement, LinearScale);

export default {
    name: 'PetExerciseComponent',
    components:{
        ModalAddExercise,
        ModalEditDeleteExercise,
        DatePicker,
        Bar
    },
    mixins: [
        GoogleAnalyticsMixins,
        toastMessageMixins,
        userMixins,
        tableMixins,
        switchPetMixins,
        dateMixins
    ],
    computed: {
        graphWidth() {
            let width = 600;
            if (this.windowWidth <= 767) {
                width = 300;
            }
            return width;
        },
    },
    data() {
        return {
            theads: [
                { name: null, sortLabel: null, isDesc: false, style: 'width: 10%;'},
                { name: 'On Leash', sortLabel: null, isDesc: false, style: 'width: 28%;'},
                { name: 'Off Leash', sortLabel: null, isDesc: false, style: 'width: 28%;'},
                { name: 'Date', sortLabel: null, style: 'width: 34%;', hasDateSearch: true, searchLabel: 'dateRange'}
            ],
            isAPILoading: false,
            summary: {},
            selectedDate: {},
            windowWidth: window.innerWidth,
            // Chart data
            chartData: {
                labels: [],
                datasets: [
                    {
                        label: 'ON LEASH',
                        backgroundColor: '#003E9C',
                        data: []
                    },
                    {
                        label: 'OFF LEASH',
                        backgroundColor: '#FF732C',
                        data: []
                    }
                ]
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                      stacked: true,
                    },
                    y: {
                      stacked: true,
                    }
                }
            }
        }
    },
    created() {
        this.summary.dateEndsAt = this.formatDate(new Date());
        this.summary.dateStartsAt = this.addDaysToDate(new Date(), -7);
        this.columnsFilter['f_dateEndsAt'] = this.dateToISO(new Date());
        this.columnsFilter['f_dateStartsAt'] = this.dateToISO(this.addDaysToDate(new Date(), -7));
        this.getTableContent();
        window.addEventListener('resize', this.onResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },
    methods: {
        onResize() {
            this.windowWidth = window.innerWidth;
        },
        refreshActivePetData() {
            this.getTableContent();
        },
        showAddExerciseModal() {
            this.$refs.modalAddExercise.show();
        },
        showEditDeleteExerciseModal(exercise) {
            this.$refs.modalEditDeleteExercise.show(exercise);
        },
        getTableContent(page = this.page, limit = this.isDashboard ? 5 : this.limit, sortBy = this.sortBy, desc = this.desc) {
            this.getExerciseList({ page, limit, sortBy, desc });
        },
        getExerciseList(queryParams) {
            this.isAPILoading = true;
            queryParams['f_petId'] = this.getActivePet.id;

            if(this.columnsFilter['f_dateStartsAt']) {
                queryParams['f_dateStartsAt'] = this.formatDate(this.columnsFilter['f_dateStartsAt']);
            }

            if(this.columnsFilter['f_dateEndsAt']) {
                queryParams['f_dateEndsAt'] = this.formatDate(this.columnsFilter['f_dateEndsAt']);
            }

            exerciseService.getExercise(queryParams)
                .then(res => {
                    if (res.status === STATUS_CODES.SUCCESS) {
                        this.tableContent = {
                            tableData: res.data.data,
                            paginationData: res.data.pagination
                        };

                        this.summary = {
                            ...this.summary,
                            totalOnLeash: res.data.exerciseSummary.totalOnLeash,
                            totalOffLeash: res.data.exerciseSummary.totalOffLeash
                        }

                        this.chartData.labels = [];
                        this.chartData.datasets[0].data = [];
                        this.chartData.datasets[1].data = [];

                        if (res.data.data.length > 0) {
                            for (const i of res.data.data) {
                                this.chartData.labels.push(this.formatDate(i.exerciseDate, 'MMM dd'));
                                this.chartData.datasets[0].data.push(i.onLeash);
                                this.chartData.datasets[1].data.push(i.offLeash);
                            }
                        }

                        if (this.columnsFilter['f_dateEndsAt']) {
                            this.summary.dateStartsAt = this.formatDate(this.columnsFilter['f_dateStartsAt']);
                            this.summary.dateEndsAt = this.formatDate(this.columnsFilter['f_dateEndsAt']);
                        } else {
                            this.summary.dateEndsAt = this.formatDate(new Date());
                            this.summary.dateStartsAt = this.addDaysToDate(new Date(), -7);
                        }

                        this.isAPILoading = false;
                    } else if (res.status !== STATUS_CODES.UNAUTHORIZED) {
                        this.isAPILoading = false;
                        const { ERROR_GET_TREATMENT_LIST } = this.toastMessages.treatment();
                        this.showAPIErrorToast(ERROR_GET_TREATMENT_LIST, res);
                    }
                });
        },
        addExercise(){
            this.getTableContent();
        },
        editDeleteExercise(){
            this.getTableContent();
        },
        timeConvert(mins){
            let hours = (mins / 60);
            let tempHours = Math.floor(hours);
            let minutes = (hours - tempHours) * 60;
            let tempMinutes = Math.round(minutes);
            
            if (tempHours === 0) {
                return tempMinutes > 1 ?  `${tempMinutes} minutes` : `${tempMinutes} minute`;
            } else if (tempMinutes === 0) {
                return tempHours > 1 ?  `${tempHours} hours` : `${tempHours} hour`;
            } else {
                tempMinutes = tempMinutes > 1 ?  `${tempMinutes} minutes` : `${tempMinutes} minute`;
                tempHours = tempHours > 1 ?  `${tempHours} hours ` : `${tempHours} hour `;
                return tempHours + tempMinutes;
            }
        }
    }
}